/**
 * Purchase orders.
 *
 * ═══════════════════════════════════════════════════════════════════
 * ISSUING AN ORDER IS THE MOMENT THE ORGANISATION BECOMES COMMITTED.
 *
 * Before it, the budget carries a SOFT commitment: an intention, which can be
 * withdrawn. On issue it becomes a HARD commitment, because a supplier has been
 * told to supply and the organisation is contractually exposed. Receiving the
 * goods does not relieve it — only the invoice does, because until the invoice
 * is posted the liability is still an estimate.
 *
 * That distinction is what makes the available-budget figure on a budget
 * holder's screen honest, so the interface names it rather than hiding it.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTenant, useTerms, lower } from '../lib/tenant';
import { date, dateTime, money, statusLabel, statusTone, todayIso, type BadgeTone } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  TextInput,
} from '../components/ui';

/**
 * The statuses a purchase order can actually hold.
 *
 * This is the CHECK constraint on purchase_order, in the order an order moves
 * through them. Offering a filter value the column cannot hold produces an
 * empty table that looks like a real answer, which is worse than not offering
 * the filter at all.
 */
const ORDER_STATUSES = [
  'draft',
  'issued',
  'acknowledged',
  'part_received',
  'received',
  'part_invoiced',
  'invoiced',
  'completed',
  'on_hold',
  'cancelled',
] as const;

/**
 * Badge tone for an order status.
 *
 * The shared map has no entry for 'invoiced', so the most advanced state an
 * order reaches would otherwise wear the same grey as a draft — the finished
 * work looking less progressed than the unfinished. Overridden here until the
 * shared map carries it.
 */
function orderTone(status: string): BadgeTone {
  return status === 'invoiced' ? 'ok' : statusTone(status);
}

/**
 * Match policy in the words procurement and audit use.
 *
 * The generic humaniser turns 'three_way' into "Three way". The term of art is
 * hyphenated, and unhyphenated it reads like a machine value somebody only
 * half-translated.
 */
function matchPolicyLabel(policy: string | null | undefined): string {
  if (!policy) return '—';
  const map: Record<string, string> = { two_way: 'Two-way', three_way: 'Three-way', four_way: 'Four-way' };
  return map[policy] ?? statusLabel(policy);
}

interface OrderRow {
  id: string;
  order_no: string;
  order_date: string;
  status: string;
  gross_total_minor: string | number;
  commitment_minor: number;
  supplier_name: string;
  supplier_code: string;
  requisition_no: string | null;
  issued_at: string | null;
  receipt_count: string | number;
  invoice_count: string | number;
  requires_goods_receipt: boolean;
  match_policy: string;
}

export function OrdersPage() {
  const t = useTerms();
  const [status, setStatus] = useState('');
  const list = useAsync(
    () => api.get<{ orders: OrderRow[] }>(`/procurement/orders${status ? `?status=${status}` : ''}`),
    [status],
  );

  const rows = list.data?.orders ?? [];

  return (
    <>
      <PageHeader
        title={t.purchaseOrderPlural}
        subtitle={`An instruction to a ${lower(t.supplier)} to supply. Once issued the commitment against the budget is hard.`}
      />

      <Card
        title={t.purchaseOrderPlural}
        flush
        actions={
          <label className="row" style={{ gap: '0.4rem' }}>
            <span className="smaller muted">Status</span>
            <select value={status} onChange={(e) => setStatus(e.currentTarget.value)}>
              <option value="">All</option>
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          </label>
        }
        footer={
          rows.length > 0 ? (
            <p className="smaller muted" style={{ margin: 0 }}>
              Value is the gross order total. Still committed is what the order is holding against the budget, which
              is the net figure — VAT is accounted for separately.
            </p>
          ) : undefined
        }
      >
        <AsyncPanel state={list}>
          {(data) => (
            <DataTable
              caption={`${t.purchaseOrderPlural} in this ${lower(t.entity)}`}
              rows={data.orders}
              rowKey={(o) => o.id}
              emptyMessage={`No ${lower(t.purchaseOrderPlural)} match this filter.`}
              columns={[
                {
                  key: 'no',
                  header: 'Number',
                  sortValue: (o) => o.order_no,
                  render: (o) => (
                    <Link to={`/orders/${o.id}`}>
                      {/* A document reference broken across two lines reads as a
                          layout nobody looked at, so it never breaks. */}
                      <span className="mono" style={{ whiteSpace: 'nowrap' }}>
                        {o.order_no}
                      </span>
                    </Link>
                  ),
                },
                {
                  key: 'supplier',
                  header: t.supplier,
                  sortValue: (o) => o.supplier_name,
                  render: (o) => (
                    <>
                      <div className="truncate">{o.supplier_name}</div>
                      {o.requisition_no ? (
                        <div className="smaller muted mono">from {o.requisition_no}</div>
                      ) : null}
                    </>
                  ),
                },
                {
                  key: 'date',
                  header: 'Ordered',
                  sortValue: (o) => o.order_date,
                  render: (o) => <span className="smaller">{date(o.order_date)}</span>,
                },
                {
                  key: 'value',
                  header: 'Value',
                  numeric: true,
                  sortValue: (o) => Number(o.gross_total_minor),
                  render: (o) => <Money minor={Number(o.gross_total_minor)} />,
                },
                {
                  key: 'commitment',
                  // The basis is named in the header because the figure beside it is
                  // gross: without it the pair reads as an unexplained difference.
                  // Abbreviated to "net" and spelled out in the note under the table,
                  // because a header wide enough for "net of VAT" squeezes the order
                  // number into two lines on a 1280-wide screen.
                  header: 'Still committed (net)',
                  numeric: true,
                  sortValue: (o) => o.commitment_minor,
                  render: (o) => <Money minor={o.commitment_minor} blankIfZero />,
                },
                {
                  key: 'progress',
                  header: 'Progress',
                  // Stacked rather than joined by a separator: the column is narrow
                  // enough to wrap, and a wrapped "·" left dangling at the end of a
                  // line is the sort of detail a projector magnifies.
                  render: (o) => (
                    <>
                      <div className="smaller muted">
                        {Number(o.receipt_count)} receipt{Number(o.receipt_count) === 1 ? '' : 's'}
                      </div>
                      <div className="smaller muted">
                        {Number(o.invoice_count)} invoice{Number(o.invoice_count) === 1 ? '' : 's'}
                      </div>
                    </>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  sortValue: (o) => statusLabel(o.status),
                  render: (o) => <Badge tone={orderTone(o.status)}>{statusLabel(o.status)}</Badge>,
                },
              ]}
              /*
               * Sorting but no search box: the totals below are summed from the
               * rows the server returned, so a client-side search would leave a
               * total on screen that no longer matched the rows above it. The
               * status filter re-queries, so those totals stay honest.
               */
              footer={
                <tr className="is-total">
                  <td colSpan={3}>
                    {data.orders.length}{' '}
                    {data.orders.length === 1 ? lower(t.purchaseOrder) : lower(t.purchaseOrderPlural)}
                  </td>
                  <td className="num">{money(data.orders.reduce((s, o) => s + Number(o.gross_total_minor), 0))}</td>
                  <td className="num">{money(data.orders.reduce((s, o) => s + o.commitment_minor, 0))}</td>
                  <td colSpan={2} />
                </tr>
              }
            />
          )}
        </AsyncPanel>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Order detail — the drill-down spine of purchase to pay
 * ------------------------------------------------------------------ */

interface OrderTrail {
  order: Record<string, unknown> & {
    id: string;
    order_no: string;
    status: string;
    order_date: string;
    supplier_id: string;
    supplier_name: string;
    supplier_code: string;
    requisition_no: string | null;
    requisition_id: string | null;
    net_total_minor: string | number;
    vat_total_minor: string | number;
    gross_total_minor: string | number;
    issued_at: string | null;
    issue_method: string | null;
    issued_by_name: string | null;
    created_by_name: string | null;
    requires_goods_receipt: boolean;
    match_policy: string;
    date_required: string | null;
    delivery_address: string | null;
  };
  lines: Array<
    Record<string, unknown> & {
      id: string;
      line_no: number;
      description: string;
      quantity: string;
      quantity_received: string;
      quantity_invoiced: string;
      unit_price_minor: string | number;
      line_net_minor: string | number;
      line_status: string;
      account_code: string;
      cost_centre_code: string;
      project_code: string | null;
    }
  >;
  receipts: Array<Record<string, unknown> & { id: string; grn_no: string; receipt_date: string; status: string; received_by_name: string; value_minor: string | number; delivery_note_ref: string | null }>;
  invoices: Array<{ id: string; invoice_no: string; supplier_reference: string; invoice_date: string; gross_total_minor: string | number; status: string; match_status: string; journal_id: string | null }>;
  amendments: Array<Record<string, unknown> & { id: string; revision_no: number; reason: string; amended_by_name: string | null; amended_at: string }>;
  commitments: Array<Record<string, unknown> & { id: string; commitment_type: string; status: string; original_amount_minor: string | number; outstanding_amount_minor: string | number }>;
}

/** What the server hands back when an order is issued. */
interface IssueResult {
  orderNo: string;
  issuedTo: string;
  revisionNo: number;
  notice: string | null;
}

/** What the server hands back when a goods receipt is recorded. */
interface ReceiptResult {
  id: string;
  grnNo: string;
  orderNo: string;
  orderStatus: string;
  /** The goods-received-not-invoiced accrual posted alongside the receipt. */
  accrualJournalId: string | null;
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const t = useTerms();
  const { isDemonstration } = useTenant();
  const { can } = useSession();
  const detail = useAsync(() => api.get<OrderTrail>(`/procurement/orders/${id}`), [id]);

  const [issuing, setIssuing] = useState(false);
  const [receipting, setReceipting] = useState(false);
  const [receiptDate, setReceiptDate] = useState(todayIso());
  const [deliveryNote, setDeliveryNote] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [showDocument, setShowDocument] = useState(false);
  const [issued, setIssued] = useState<IssueResult | null>(null);
  const [received, setReceived] = useState<ReceiptResult | null>(null);

  const issue = useMutation(async () =>
    api.post<IssueResult>(`/procurement/orders/${id}/issue`, { method: 'email' }),
  );

  const receipt = useMutation(async (lines: Array<{ purchaseOrderLineId: string; quantity: string }>) =>
    api.post<ReceiptResult>(`/procurement/orders/${id}/receipts`, {
      receiptDate,
      deliveryNoteRef: deliveryNote.trim() || null,
      lines: lines.map((l) => ({ ...l, condition: 'good' as const })),
    }),
  );

  // The printable document replaces the screen rather than opening over it, so
  // that what the browser prints is the document and nothing else.
  if (showDocument) {
    return <OrderDocumentView orderId={id ?? ''} onClose={() => setShowDocument(false)} />;
  }

  return (
    <AsyncPanel state={detail}>
      {(data) => {
        const o = data.order;
        const outstandingLines = data.lines.filter(
          (l) => l.line_status !== 'cancelled' && Number(l.quantity) - Number(l.quantity_received) > 0.0001,
        );
        const openCommitments = data.commitments.filter(
          (c) => c.status === 'open' || c.status === 'partially_relieved',
        );
        const openCommitment = openCommitments.reduce((s, c) => s + Number(c.outstanding_amount_minor), 0);
        const allRelieved = data.commitments.length > 0 && data.commitments.every((c) => c.status === 'relieved');
        // Nothing to post is not an error to be discovered by the server: the
        // button simply does not arm until there is a quantity to record.
        const receivable = Object.values(quantities).filter((q) => Number(q) > 0).length;
        const canReceipt = o.status !== 'draft' && o.status !== 'cancelled' && outstandingLines.length > 0;

        return (
          <>
            <PageHeader
              title={`${t.purchaseOrder} ${o.order_no}`}
              subtitle={
                <>
                  {o.supplier_name}
                  {o.requisition_no ? (
                    <>
                      {' · from '}
                      <Link to={`/requisitions/${o.requisition_id}`}>
                        <span className="mono">{o.requisition_no}</span>
                      </Link>
                    </>
                  ) : null}
                </>
              }
              meta={
                <>
                  <Badge tone={orderTone(o.status)}>{statusLabel(o.status)}</Badge>
                  <span>Ordered {date(o.order_date)}</span>
                  {o.issued_at ? <span>Issued {dateTime(o.issued_at)}</span> : null}
                  <Badge tone="neutral">{matchPolicyLabel(o.match_policy)} matching</Badge>
                </>
              }
              actions={
                <div className="btn-group">
                  {o.status === 'draft' && can('po.issue') ? (
                    <button type="button" className="btn btn--primary" onClick={() => setIssuing(true)}>
                      Issue to {lower(t.supplier)}
                    </button>
                  ) : null}
                  {canReceipt && can('grn.record') ? (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setQuantities(
                          Object.fromEntries(
                            outstandingLines.map((l) => [
                              l.id,
                              String(Number(l.quantity) - Number(l.quantity_received)),
                            ]),
                          ),
                        );
                        setReceipting(true);
                      }}
                    >
                      Record a goods receipt
                    </button>
                  ) : null}
                  <button type="button" className="btn btn--ghost" onClick={() => setShowDocument(true)}>
                    View the order document
                  </button>
                </div>
              }
            />

            {issued ? (
              <Alert tone="ok" title={`${t.purchaseOrder} ${issued.orderNo} has been issued`}>
                <p>
                  The soft commitment is now hard: the {lower(t.supplier)} has been instructed to supply, and the
                  budget carries the exposure until the invoice is posted.
                </p>
                {issued.notice ? <p className="smaller muted">{issued.notice}</p> : null}
              </Alert>
            ) : null}

            {received ? (
              <Alert tone="ok" title={`Goods receipt ${received.grnNo} recorded`}>
                <p>
                  {t.purchaseOrder} {received.orderNo} is now{' '}
                  <Badge tone={orderTone(received.orderStatus)}>{statusLabel(received.orderStatus)}</Badge>.{' '}
                  {received.accrualJournalId ? (
                    <>
                      The accrual for goods received but not yet invoiced has been posted —{' '}
                      <Link to={`/ledger/journals/${received.accrualJournalId}`}>view the journal</Link>.
                    </>
                  ) : null}
                </p>
              </Alert>
            ) : null}

            {openCommitment > 0 ? (
              <Alert tone="info" title="Committed against the budget">
                <p>
                  <Money minor={openCommitment} /> is still committed on this order. The commitment is held net of
                  VAT — the budget carries the net cost and VAT is accounted for separately, which is why it is
                  lower than the order total. It is relieved when the supplier invoice is posted, not when the goods
                  arrive — receipting creates an accrual instead, so the expenditure appears in the period the goods
                  were delivered.
                </p>
              </Alert>
            ) : null}

            <div className="grid grid--3">
              <div style={{ gridColumn: 'span 2' }} className="stack">
                <Card title="Lines" flush>
                  <DataTable
                    caption={`Lines on ${o.order_no}`}
                    rows={data.lines}
                    rowKey={(l) => l.id}
                    columns={[
                      { key: 'no', header: '#', width: '3rem', render: (l) => l.line_no },
                      { key: 'desc', header: 'Description', render: (l) => l.description },
                      {
                        key: 'coding',
                        header: 'Coding',
                        render: (l) => (
                          <span className="mono smaller">
                            {l.account_code} / {l.cost_centre_code}
                            {l.project_code ? ` / ${l.project_code}` : ''}
                          </span>
                        ),
                      },
                      { key: 'qty', header: 'Ordered', numeric: true, render: (l) => l.quantity },
                      { key: 'recd', header: 'Received', numeric: true, render: (l) => l.quantity_received },
                      { key: 'inv', header: 'Invoiced', numeric: true, render: (l) => l.quantity_invoiced },
                      { key: 'net', header: 'Net', numeric: true, render: (l) => <Money minor={Number(l.line_net_minor)} /> },
                    ]}
                    footer={
                      <>
                        <tr className="is-subtotal">
                          <td colSpan={6}>Net</td>
                          <td className="num">{money(Number(o.net_total_minor))}</td>
                        </tr>
                        <tr className="is-subtotal">
                          <td colSpan={6}>VAT</td>
                          <td className="num">{money(Number(o.vat_total_minor))}</td>
                        </tr>
                        <tr className="is-total">
                          <td colSpan={6}>Total</td>
                          <td className="num">{money(Number(o.gross_total_minor))}</td>
                        </tr>
                      </>
                    }
                  />
                </Card>

                <Card title="Goods receipts" flush>
                  {data.receipts.length === 0 ? (
                    <div style={{ padding: '1rem' }}>
                      <EmptyState title="Nothing received yet" />
                    </div>
                  ) : (
                    <DataTable
                      caption={`Goods receipts against ${o.order_no}`}
                      rows={data.receipts}
                      rowKey={(r) => r.id}
                      columns={[
                        {
                          key: 'no',
                          header: 'GRN',
                          render: (r) => (
                            <span className="mono" style={{ whiteSpace: 'nowrap' }}>
                              {r.grn_no}
                            </span>
                          ),
                        },
                        { key: 'date', header: 'Received', render: (r) => date(r.receipt_date) },
                        { key: 'by', header: 'By', render: (r) => <span className="smaller">{r.received_by_name}</span> },
                        {
                          key: 'note',
                          header: 'Delivery note',
                          render: (r) => <span className="mono smaller">{r.delivery_note_ref ?? '—'}</span>,
                        },
                        { key: 'value', header: 'Value', numeric: true, render: (r) => <Money minor={Number(r.value_minor)} /> },
                      ]}
                      footer={
                        <tr className="is-total">
                          <td colSpan={4}>Total received</td>
                          <td className="num">
                            {money(data.receipts.reduce((s, r) => s + Number(r.value_minor), 0))}
                          </td>
                        </tr>
                      }
                    />
                  )}
                </Card>

                <Card title="Invoices" flush>
                  {data.invoices.length === 0 ? (
                    <div style={{ padding: '1rem' }}>
                      <EmptyState title="No invoice received against this order yet" />
                    </div>
                  ) : (
                    <DataTable
                      caption={`Invoices matched to ${o.order_no}`}
                      rows={data.invoices}
                      rowKey={(i) => i.id}
                      columns={[
                        {
                          /*
                           * The supplier's own reference sits under the invoice number
                           * rather than in a column of its own: six columns in a
                           * two-thirds-width card push the status badge off the edge
                           * on a 1280-wide screen, and this is the pairing the orders
                           * list already uses for a document and its source.
                           */
                          key: 'no',
                          header: 'Invoice',
                          render: (i) => (
                            <>
                              <Link to={`/invoices/${i.id}`}>
                                <span className="mono" style={{ whiteSpace: 'nowrap' }}>
                                  {i.invoice_no}
                                </span>
                              </Link>
                              <div className="smaller muted mono">their ref {i.supplier_reference}</div>
                            </>
                          ),
                        },
                        { key: 'date', header: 'Dated', render: (i) => date(i.invoice_date) },
                        { key: 'value', header: 'Value', numeric: true, render: (i) => <Money minor={Number(i.gross_total_minor)} /> },
                        { key: 'match', header: 'Match', render: (i) => <Badge tone={statusTone(i.match_status)}>{statusLabel(i.match_status)}</Badge> },
                        { key: 'status', header: 'Status', render: (i) => <Badge tone={statusTone(i.status)}>{statusLabel(i.status)}</Badge> },
                      ]}
                      footer={
                        <tr className="is-total">
                          <td colSpan={2}>Total invoiced</td>
                          <td className="num">
                            {money(data.invoices.reduce((s, i) => s + Number(i.gross_total_minor), 0))}
                          </td>
                          <td colSpan={2} />
                        </tr>
                      }
                    />
                  )}
                </Card>
              </div>

              <div className="stack">
                <Card title="Order details">
                  <DefinitionList
                    items={[
                      [
                        t.supplier,
                        <Link to={`/suppliers/${o.supplier_id}`}>
                          {o.supplier_code} — {o.supplier_name}
                        </Link>,
                      ],
                      ['Raised by', o.created_by_name ?? '—'],
                      ['Issued by', o.issued_by_name ?? 'Not yet issued'],
                      ['Issue method', o.issue_method ? statusLabel(o.issue_method) : '—'],
                      ['Date required', o.date_required ? date(o.date_required) : '—'],
                      ['Goods receipt', o.requires_goods_receipt ? 'Required' : 'Not required'],
                      ['Match policy', matchPolicyLabel(o.match_policy)],
                    ]}
                  />
                  {/*
                    The despatch is recorded, not performed. "Issue method: Email" on
                    its own reads as a statement that an email went out, which is the
                    kind of quiet overclaim a procurement officer is right to test.
                  */}
                  {o.issue_method && isDemonstration ? (
                    <p className="smaller muted" style={{ margin: '0.75rem 0 0' }}>
                      Issue is recorded rather than performed here: the order document is produced and the despatch
                      logged, and nothing is sent to the {lower(t.supplier)} in this demonstration.
                    </p>
                  ) : null}
                </Card>

                {data.commitments.length > 0 ? (
                  <Card title="Commitments" subtitle="What this order is holding against the budget.">
                    {openCommitments.length === 0 ? (
                      <p className="smaller muted" style={{ margin: 0 }}>
                        {allRelieved
                          ? 'Nothing is held any more. The commitment was relieved when the supplier invoice was posted, so the budget now carries the actual cost rather than an estimate.'
                          : 'Nothing is held against the budget any more.'}
                      </p>
                    ) : (
                      <ul className="stack stack--sm">
                        {openCommitments.map((c) => (
                          <li key={c.id} className="row row--between">
                            <span>
                              <Badge tone={c.commitment_type === 'hard' ? 'warn' : 'info'}>
                                {statusLabel(c.commitment_type)}
                              </Badge>{' '}
                              <span className="smaller muted">{statusLabel(c.status)}</span>
                            </span>
                            <span>
                              <Money minor={Number(c.outstanding_amount_minor)} /> of{' '}
                              <span className="muted smaller">{money(Number(c.original_amount_minor))}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                ) : null}

                {data.amendments.length > 0 ? (
                  <Card title="Amendments">
                    <ul className="timeline">
                      {data.amendments.map((a) => (
                        <li key={a.id} className="timeline__item">
                          <p className="timeline__title">Revision {a.revision_no}</p>
                          <p className="timeline__detail">{a.reason}</p>
                          <p className="timeline__time">
                            {a.amended_by_name} · {dateTime(a.amended_at)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ) : null}
              </div>
            </div>

            <Modal
              open={issuing}
              onClose={() => setIssuing(false)}
              title={`Issue ${o.order_no} to ${o.supplier_name}`}
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setIssuing(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={issue.busy}
                    onClick={() =>
                      void issue.run().then((result) => {
                        if (result) {
                          setIssuing(false);
                          setIssued(result);
                          detail.reload();
                        }
                      })
                    }
                  >
                    {issue.busy ? 'Issuing…' : 'Issue the order'}
                  </button>
                </>
              }
            >
              <p>
                Issuing converts the soft commitment on this order into a hard commitment. The organisation becomes
                contractually exposed for <Money minor={Number(o.gross_total_minor)} />, of which{' '}
                <Money minor={Number(o.net_total_minor)} /> is held against the budget — the commitment is net of
                VAT.
              </p>
              <p className="smaller muted">
                In this demonstration the order document is produced and the despatch is recorded. No email is sent.
              </p>
              <ErrorNotice error={issue.error} />
            </Modal>

            <Modal
              open={receipting}
              onClose={() => setReceipting(false)}
              wide
              title={`Record what arrived against ${o.order_no}`}
              footer={
                <>
                  <button type="button" className="btn" onClick={() => setReceipting(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={receipt.busy || receivable === 0}
                    onClick={() =>
                      void receipt
                        .run(
                          Object.entries(quantities)
                            .filter(([, q]) => Number(q) > 0)
                            .map(([lineId, q]) => ({ purchaseOrderLineId: lineId, quantity: q })),
                        )
                        .then((result) => {
                          if (result) {
                            setReceipting(false);
                            setReceived(result);
                            detail.reload();
                          }
                        })
                    }
                  >
                    {receipt.busy ? 'Recording…' : 'Record the receipt'}
                  </button>
                </>
              }
            >
              <div className="stack">
                <div className="grid grid--2">
                  <TextInput
                    label="Date received"
                    type="date"
                    value={receiptDate}
                    onChange={(e) => setReceiptDate(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Delivery note reference"
                    value={deliveryNote}
                    onChange={(e) => setDeliveryNote(e.currentTarget.value)}
                  />
                </div>
                <div className="table-wrap">
                  <table className="data">
                    <caption className="visually-hidden">Quantities to receive</caption>
                    <thead>
                      <tr>
                        <th scope="col">Line</th>
                        <th scope="col" className="num">
                          Ordered
                        </th>
                        <th scope="col" className="num">
                          Already received
                        </th>
                        <th scope="col" className="num">
                          Receiving now
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstandingLines.map((l) => (
                        <tr key={l.id}>
                          <td>{l.description}</td>
                          <td className="num">{l.quantity}</td>
                          <td className="num">{l.quantity_received}</td>
                          <td className="num">
                            <label>
                              <span className="visually-hidden">Quantity received for {l.description}</span>
                              <input
                                className="num-input"
                                inputMode="decimal"
                                value={quantities[l.id] ?? ''}
                                /*
                                 * The typed value is read out of the event HERE and not
                                 * inside the updater below. React clears currentTarget
                                 * once the handler returns, and an updater function can
                                 * run later, during the render — at which point reading
                                 * the event throws and takes the whole screen down.
                                 */
                                onChange={(e) => {
                                  const value = e.currentTarget.value;
                                  setQuantities((q) => ({ ...q, [l.id]: value }));
                                }}
                                style={{ width: '7rem' }}
                              />
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {receivable === 0 ? (
                  <p className="smaller muted" role="status">
                    Enter a quantity against at least one line before recording the receipt.
                  </p>
                ) : null}
                <p className="smaller muted">
                  Receipting posts an accrual for goods received but not yet invoiced. It does not relieve the
                  commitment — the invoice does.
                </p>
                <ErrorNotice error={receipt.error} />
              </div>
            </Modal>
          </>
        );
      }}
    </AsyncPanel>
  );
}

/* ------------------------------------------------------------------ *
 * The order document
 * ------------------------------------------------------------------ */

interface OrderDocument {
  entity: { name: string; legal_name: string | null; vat_registration: string | null };
  order: {
    order_no: string;
    order_date: string;
    date_required: string | null;
    delivery_address: string | null;
    contact_name: string | null;
    terms_text: string | null;
    revision_no: number;
    net_total_minor: string | number;
    vat_total_minor: string | number;
    gross_total_minor: string | number;
    supplier_name: string;
    supplier_code: string;
    requisition_no: string | null;
    match_policy: string;
    requires_goods_receipt: boolean;
  };
  lines: Array<{
    id: string;
    line_no: number;
    description: string;
    quantity: string;
    unit: string | null;
    unit_price_minor: string | number;
    vat_code: string | null;
    line_net_minor: string | number;
  }>;
  supplierAddress: {
    line1: string;
    line2: string | null;
    town: string | null;
    county: string | null;
    postcode: string | null;
    country: string | null;
  } | null;
  generatedAt: string;
}

/**
 * The purchase order as a document.
 *
 * This is the artefact a procurement officer asks for first — "show me the
 * order we send the supplier" — so it is laid out as a document rather than
 * offered as a file. It replaces the detail screen instead of opening over it,
 * because the print stylesheet then produces the document on its own: the
 * navigation, the page-header buttons and every other control are already
 * hidden when printing.
 */
function OrderDocumentView({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const t = useTerms();
  const { isDemonstration } = useTenant();
  const doc = useAsync(() => api.get<OrderDocument>(`/procurement/orders/${orderId}/document`), [orderId]);

  return (
    <AsyncPanel state={doc}>
      {(d) => {
        const o = d.order;
        const address = [
          d.supplierAddress?.line1,
          d.supplierAddress?.line2,
          d.supplierAddress?.town,
          d.supplierAddress?.county,
          d.supplierAddress?.postcode,
          d.supplierAddress?.country,
        ].filter((line): line is string => Boolean(line));

        return (
          <>
            <PageHeader
              title={`${t.purchaseOrder} ${o.order_no}`}
              subtitle={`The document as the ${lower(t.supplier)} receives it.`}
              actions={
                <div className="btn-group">
                  <button type="button" className="btn" onClick={onClose}>
                    Back to the order
                  </button>
                  <button type="button" className="btn btn--primary" onClick={() => window.print()}>
                    Print
                  </button>
                </div>
              }
            />

            <Card>
              <div className="stack">
                <div className="grid grid--2">
                  <div>
                    <p className="smaller muted" style={{ margin: 0 }}>
                      From
                    </p>
                    <p style={{ margin: '0.25rem 0 0', fontWeight: 600 }}>{d.entity.legal_name ?? d.entity.name}</p>
                    {d.entity.vat_registration ? (
                      <p className="smaller muted" style={{ margin: '0.25rem 0 0' }}>
                        VAT registration {d.entity.vat_registration}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <p className="smaller muted" style={{ margin: 0 }}>
                      To
                    </p>
                    <p style={{ margin: '0.25rem 0 0', fontWeight: 600 }}>{o.supplier_name}</p>
                    {address.length > 0 ? (
                      <p className="smaller" style={{ margin: '0.25rem 0 0' }}>
                        {address.join(', ')}
                      </p>
                    ) : null}
                    <p className="smaller muted mono" style={{ margin: '0.25rem 0 0' }}>
                      {o.supplier_code}
                    </p>
                  </div>
                </div>

                <DefinitionList
                  items={[
                    [`${t.purchaseOrder} number`, <span className="mono">{o.order_no}</span>],
                    ['Order date', date(o.order_date)],
                    ['Required by', o.date_required ? date(o.date_required) : 'As agreed'],
                    ['Revision', String(o.revision_no)],
                    [
                      `Raised from ${lower(t.requisition)}`,
                      o.requisition_no ? <span className="mono">{o.requisition_no}</span> : '—',
                    ],
                    ['Deliver to', o.delivery_address ?? d.entity.name],
                  ]}
                />

                <DataTable
                  caption={`Lines on ${o.order_no}`}
                  rows={d.lines}
                  rowKey={(l) => l.id}
                  columns={[
                    { key: 'no', header: '#', width: '3rem', render: (l) => l.line_no },
                    { key: 'desc', header: 'Description', render: (l) => l.description },
                    {
                      key: 'qty',
                      header: 'Quantity',
                      numeric: true,
                      render: (l) => (l.unit ? `${l.quantity} ${l.unit}` : l.quantity),
                    },
                    {
                      key: 'price',
                      header: 'Unit price',
                      numeric: true,
                      render: (l) => <Money minor={Number(l.unit_price_minor)} />,
                    },
                    { key: 'vat', header: 'VAT code', render: (l) => <span className="mono smaller">{l.vat_code ?? '—'}</span> },
                    { key: 'net', header: 'Net', numeric: true, render: (l) => <Money minor={Number(l.line_net_minor)} /> },
                  ]}
                  footer={
                    <>
                      <tr className="is-subtotal">
                        <td colSpan={5}>Net</td>
                        <td className="num">{money(Number(o.net_total_minor))}</td>
                      </tr>
                      <tr className="is-subtotal">
                        <td colSpan={5}>VAT</td>
                        <td className="num">{money(Number(o.vat_total_minor))}</td>
                      </tr>
                      <tr className="is-total">
                        <td colSpan={5}>Total</td>
                        <td className="num">{money(Number(o.gross_total_minor))}</td>
                      </tr>
                    </>
                  }
                />

                {o.terms_text ? <p className="smaller">{o.terms_text}</p> : null}

                <p className="smaller muted" style={{ margin: 0 }}>
                  Quote {o.order_no} on all invoices and delivery notes. Invoices are matched{' '}
                  {lower(matchPolicyLabel(o.match_policy))}
                  {o.requires_goods_receipt ? ' against a recorded goods receipt' : ''} before payment.
                  {o.contact_name ? ` Order contact: ${o.contact_name}.` : ''}
                </p>
                <p className="smaller muted" style={{ margin: 0 }}>
                  Produced {dateTime(d.generatedAt)}.
                </p>

                {isDemonstration ? (
                  <Alert tone="warn" title="Demonstration document">
                    <p>
                      Produced in a demonstration environment from synthetic data. It has not been sent to any{' '}
                      {lower(t.supplier)}.
                    </p>
                  </Alert>
                ) : null}
              </div>
            </Card>
          </>
        );
      }}
    </AsyncPanel>
  );
}
